import { requestAsync } from 'redux-query';
import get from 'lodash/get';
import head from 'lodash/head';
import oddslib from 'oddslib';
import { createAction, createState } from 'stateducer';
import { isNullOrUndefined } from 'dash-front/helpers';
import { UPDATE_DEFAULT } from 'dash-front/requery/actions';
import cfg from 'cfg';
import { emptyValue } from './constants';

export const selectOutcome = createAction('selectOutcome');
export const removeOutcome = createAction('removeOutcome');
export const resetSelection = createAction('resetSelection');
export const toggleSelection = createAction('toggleSelection');
export const setEventId = createAction('setEventId');
export const setCampaign = createAction('setCampaign');
export const setProvider = createAction('setProvider');

const initialOdds = {
    selected: {},
    selectedOrder: [],
    eventId: 0,
    selectedCampaign: emptyValue,
    selectedProvider: emptyValue
};

export const oddsSelection = createState(initialOdds)
    .on(selectOutcome, ({ selected, selectedOrder, ...rest }, id) => {
        return { ...rest, selected: { ...selected, [id]: true }, selectedOrder: selectedOrder.concat(id) };
    })
    .on(removeOutcome, ({ selected, selectedOrder, ...rest }, id) => {
        return {
            ...rest,
            selected: { ...selected, [id]: false },
            selectedOrder: selectedOrder.filter(id2 => id2 !== id)
        };
    })
    .on(toggleSelection, ({ selected, selectedOrder, ...rest }, id) => {
        return selected[id]
            ? {
                  ...rest,
                  selected: { ...selected, [id]: false },
                  selectedOrder: selectedOrder.filter(id2 => id2 !== id)
              }
            : { ...rest, selected: { ...selected, [id]: true }, selectedOrder: selectedOrder.concat(id) };
    })
    .on(setEventId, (state, id) => ({
        ...state,
        selected: {},
        selectedOrder: [],
        eventId: id
    }))
    .on(setCampaign, (state, id) => ({ ...state, selectedCampaign: id }))
    .on(setProvider, (state, id) => ({
        ...state,
        selected: {},
        selectedOrder: [],
        selectedProvider: id
    }))
    .on(resetSelection, () => initialOdds);

export const oddNameSelector = (eventId, market, provider) => `${eventId}/${String(provider)}/${market}`;

export const getOutcomeRef = outcome => {
    if (!outcome) {
        throw new TypeError('Outcome is not an object while creating a reference to it.');
    }
    if (typeof outcome === 'symbol') {
        return outcome;
    }
    const { value, type } = outcome;
    return isNullOrUndefined(value) ? String(type) : `${type}/${value}`;
};

const getOddsConverter = oddsFormat => {
    switch (oddsFormat) {
        case 'fractional': {
            return oddValue => {
                try {
                    return oddslib.from('decimal', oddValue).to('fractional');
                } catch (e) {
                    return '-';
                }
            };
        }
        default:
            return v => v;
    }
};

const transformOdds = (oddsFormat, market, body, provider) => {
    // api returns an array for some reasons
    const firstOdd = get(body, ['eventsOdds', 0]);
    if (!firstOdd) {
        return null;
    }
    const converter = getOddsConverter(oddsFormat);
    const { betOffers, ...odd } = firstOdd;
    const id = `${odd.eventId}/${provider}/${market}`;
    const values = betOffers.reduce(
        (acc, fullBetOffer, index) => {
            const { outcomes, ...betOffer } = fullBetOffer;

            const betOfferID = `${id}/${index}`;

            const outcomesObject = outcomes.reduce((nextOut, outcome) => {
                const outcomeId = `${id}/${getOutcomeRef(outcome)}`;
                nextOut[outcomeId] = {
                    ...outcome,
                    odds: converter(outcome.odds),
                    internalBetofferId: betOfferID
                };
                // if (outcome.id) nextOut[outcomeId].id = outcome.id
                return nextOut;
            }, {});

            acc.outcomes = {
                ...acc.outcomes,
                ...outcomesObject
            };

            acc.betOffers[betOfferID] = {
                ...betOffer,
                outcomes: Object.keys(outcomesObject)
            };

            return acc;
        },
        {
            betOffers: {},
            outcomes: {}
        }
    );
    return {
        betOffers: values.betOffers,
        outcomes: values.outcomes,
        odds: {
            [id]: {
                ...odd,
                lastUpdateAt: get(head(Object.values(values.betOffers)), 'lastUpdateAt'),
                betOffers: Object.keys(values.betOffers)
            }
        }
    };
};

export const fetchOdds = (eventId, market, provider) => (dispatch, getState) => {
    const oddsKey = oddNameSelector(eventId, market, provider);
    const oddsFormat = get(getState().user, ['props', 'oddsFormat']);
    return dispatch(
        requestAsync({
            url: `${cfg.apis.ss2}/odds/${market}`,
            queryKey: oddsKey,
            body: {
                provider,
                eventId
            },
            transform: body => transformOdds(oddsFormat, market, body, provider),
            update: {
                odds: UPDATE_DEFAULT.MERGE,
                betOffers: UPDATE_DEFAULT.MERGE,
                outcomes: UPDATE_DEFAULT.MERGE
            },
            force: true
        })
    );
};



/** WEBPACK FOOTER **
 ** ../src/dash/front/odds/actions.js
 **/